import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import Highlighter from 'react-highlight-words';
import './ResponsiveFilterBar.scss';
import { AppliedFiltersWithContext } from './AppliedFilters';
import CSSTransition from 'react-transition-group/CSSTransition';
import { FilterSwitch, FilterSwitchState } from './FilterSwitch';
import {
    FilterContextProvider,
    FilterContextProviderParams,
    useFilterContext,
    useFilterContextForCategory
} from './FilterContext';
import { FilterSetting } from '../../store/api';
import { FilterQuickSearch } from './FilterQuickSearch';
import {
    CurrencyFilterRangeEntry,
    DateFilterRangeEntry,
    FloatingPointFilterRangeEntry,
    IntegerFilterRangeEntry,
    MonthsFilterRangeEntry
} from './FilterRangeEntry';
import { useAggregationsQuickSearchQuery } from './useAggregationsQuickSearchQuery';
import { CUSTOM_FILTER_KEY, FilterGroupContextProvider, useFilterGroupContext } from './FilterGroupContext';
import useDebounce from '../hooks/useDebounce';
import { AggregationBucket } from '../../../models/Aggregations';
import { useFilterGroup } from './useFilterGroup';
import { useSeeAllFilterEntry } from './useSeeAllFilterEntry';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    durationMapper,
    mapCollectionQuickFilter,
    mapFilterGroupKey,
    mapGenreFilterLabel,
    mapPriceFilterLabel,
    mapPagesFilterLabel,
    mapReadableUsageConditionFilterLabel,
    mapReleasedFilterLabel,
    mapReleaseFrequencyFilterLabel,
    reservesMapper
} from '../common/translationMappers/filterbarLabelMapper';

export type AggregationsProps = FilterContextProviderParams;

export const ResponsiveFilterBar: FC<AggregationsProps> = props => {
    const { t } = useTranslation();

    return (
        <div className="filter-bar-reactive">
            <FilterContextProvider
                visibleFilterGroups={props.visibleFilterGroups}
                buildQueryKey={props.buildQueryKey}
                fetchAggregations={props.fetchAggregations}
                fetchAggregationsQuickSearch={props.fetchAggregationsQuickSearch}
            >
                <AppliedFiltersWithContext
                    bucketLabelMappers={{
                        collectionQuickfilters: mapCollectionQuickFilter,
                        genre: mapGenreFilterLabel,
                        releaseFrequency: mapReleaseFrequencyFilterLabel,
                        stockUsageCondition: mapReadableUsageConditionFilterLabel,
                        selectedUsageCondition: mapReadableUsageConditionFilterLabel,
                        multiUsageCondition: mapReadableUsageConditionFilterLabel,
                        releaseDate: mapReleasedFilterLabel,
                        pages: mapPagesFilterLabel,
                        mucPrice: mapPriceFilterLabel,
                        price: mapPriceFilterLabel,
                        selectedPrice: mapPriceFilterLabel,
                        reserves: reservesMapper,
                        duration: durationMapper
                    }}
                />
                <FilterGroup category="quickfilters" collapsibleGroup={false} />
                <FilterGroup category="campaignStatus" />
                <FilterGroup category="status" />
                <FilterGroup
                    category="collectionQuickfilters"
                    collapsibleGroup={false}
                    bucketLabelMapper={mapCollectionQuickFilter}
                />
                <FilterGroup category="reserves">
                    <IntegerFilterRangeEntry
                        filterKey={CUSTOM_FILTER_KEY}
                        placeholder={t('components.filterBar.filterRangeEntry.placeholder.reserves')}
                    />
                </FilterGroup>
                <FilterGroup category="loanFormat" />
                <FilterGroup category="ageCategory" />
                <FilterGroup category="ageGroup" />
                <FilterGroup
                    category="genre"
                    searchable="client"
                    expandableResults={true}
                    bucketLabelMapper={mapGenreFilterLabel}
                />
                <FilterGroup category="explicit" />
                <FilterGroup category="series" searchable="server" />
                <FilterGroup category="author" searchable="server" />
                <FilterGroup category="authorCountry" searchable="client" expandableResults={true} />
                <FilterGroup category="narrator" searchable="server" />
                <FilterGroup category="bestseller" expandableResults={true} />
                <FilterGroup category="award" searchable="server" />
                <FilterGroup category="awardYear" expandableResults={true} />
                <FilterGroup category="selection" expandableResults={true} />
                <FilterGroup category="releaseDate">
                    {/*
                    These two filter ranges in one filter group have a unique handling:
                    - There is a difference between the values * and 0!
                        0 -> includes all releases from today in the past
                        * -> includes all future releases
                    - Since there are multiple filter ranges, the customIndex from the backend response identifies the range
                    */}
                    <MonthsFilterRangeEntry customIndex={0} />
                    <DateFilterRangeEntry customIndex={1} />
                </FilterGroup>
                <FilterGroup category="releaseFrequency" bucketLabelMapper={mapReleaseFrequencyFilterLabel} />
                <FilterGroup category="releaseQuantity" />
                <FilterGroup category="pages">
                    <IntegerFilterRangeEntry
                        filterKey={CUSTOM_FILTER_KEY}
                        placeholder={t('components.filterBar.filterRangeEntry.placeholder.pages')}
                    />
                </FilterGroup>
                <FilterGroup category="duration">
                    <FloatingPointFilterRangeEntry
                        filterKey={CUSTOM_FILTER_KEY}
                        placeholder={t('components.filterBar.filterRangeEntry.placeholder.hours')}
                    />
                </FilterGroup>
                <FilterGroup category="productionFormat" />
                <FilterGroup category="illustrated" />
                <FilterGroup category="publisher" searchable="server" />
                <FilterGroup category="publisherImprint" searchable="server" />
                <FilterGroup category="lendingModel" />
                <FilterGroup category="selectedLendingModel" />
                <FilterGroup category="stockUsageCondition" bucketLabelMapper={mapReadableUsageConditionFilterLabel} />
                <FilterGroup category="multiUsageCondition" bucketLabelMapper={mapReadableUsageConditionFilterLabel} />
                <FilterGroup
                    category="selectedUsageCondition"
                    bucketLabelMapper={mapReadableUsageConditionFilterLabel}
                />
                <FilterGroup category="language" />
                <FilterGroup category="price">
                    <CurrencyFilterRangeEntry filterKey={CUSTOM_FILTER_KEY} />
                </FilterGroup>
                <FilterGroup category="mucPrice">
                    <CurrencyFilterRangeEntry filterKey={CUSTOM_FILTER_KEY} />
                </FilterGroup>
                <FilterGroup category="selectedPrice" bucketLabelMapper={mapPriceFilterLabel}>
                    <CurrencyFilterRangeEntry filterKey={CUSTOM_FILTER_KEY} />
                </FilterGroup>
                <FilterGroup
                    category="contentProvider"
                    searchable="server"
                    titleIcon={<i className="bolinda-icons">bolinda_only</i>}
                />
                <FilterGroup category="productFormat" titleIcon={<i className="bolinda-icons">bolinda_only</i>} />
            </FilterContextProvider>
        </div>
    );
};

type FilterGroupProps = {
    readonly children?: ReactNode;
    readonly titleIcon?: ReactNode;

    readonly category: string;

    /**
     * the filter group can be opened and closed
     */
    readonly collapsibleGroup?: boolean;

    /**
     * should show a search input for quick searches inside the category
     */
    readonly searchable?: 'server' | 'client';

    /**
     * when there are more results available it should be possible to include them all
     */
    readonly expandableResults?: boolean;

    bucketLabelMapper?: (t: TFunction, bucket: AggregationBucket) => string;
};

const FilterGroup: FC<FilterGroupProps> = props => {
    const {
        category,
        collapsibleGroup = true,
        searchable = false,
        expandableResults = false,
        titleIcon = <></>,
        children
    } = props;

    const { productFilter, fetchAggregationsQuickSearch } = useFilterContext();
    const { aggregation: filterAggregation, filter } = useFilterContextForCategory(category);
    const { expanded, toggleGroup } = useFilterGroup(category, collapsibleGroup);
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const { seeAll, toggleSeeAll } = useSeeAllFilterEntry(category);

    const bucketLabelMapper = (bucket: AggregationBucket) =>
        props.bucketLabelMapper
            ? props.bucketLabelMapper(t, bucket)
            : defaultBucketLabelMapper(t, category, bucket.key, bucket.value);

    const initialShowNumberOfBuckets = 5;
    const activeFiltersCount = filter.includeFilterValues?.length ?? 0;

    const isSearchActive = searchQuery.length > 0;
    const [debouncedSearchQuery] = useDebounce(searchQuery, 250);
    const resetSearchQuery = useCallback(() => setSearchQuery(''), []);

    const aggregationsQuickSearchQuery = useAggregationsQuickSearchQuery({
        filterKey: category,
        filterMap: productFilter,
        searchQuery: searchable === 'server' ? debouncedSearchQuery : undefined,
        fetchFunc: fetchAggregationsQuickSearch
    });

    let aggregation =
        isSearchActive && searchable === 'server' && aggregationsQuickSearchQuery.data[category]
            ? aggregationsQuickSearchQuery.data[category]
            : filterAggregation;
    aggregation =
        isSearchActive && searchable === 'client'
            ? {
                  ...aggregation,
                  buckets: filterTranslatedBuckets(aggregation.buckets, debouncedSearchQuery, bucketLabelMapper)
              }
            : aggregation;

    if (!filterAggregation || !aggregation.value) {
        return null;
    }

    const visibleBuckets = activeFiltersCount + initialShowNumberOfBuckets;
    const shouldSliceBuckets =
        expandableResults && !isSearchActive && (aggregation.more || aggregation.includeAll || searchable === 'client');
    const buckets = shouldSliceBuckets ? aggregation.buckets?.slice(0, visibleBuckets) : aggregation.buckets;
    const bucketsAll = sortBy(
        aggregation.buckets?.slice(visibleBuckets, aggregation.buckets?.length),
        bucketLabelMapper
    );

    const filterGroupKey = mapFilterGroupKey(aggregation.value);

    const loading = expanded && aggregation.buckets === undefined;

    return (
        <div className={classNames('filter-group', category)}>
            <span onClick={() => toggleGroup()} className="filter-title">
                {t(`components.filterBar.filterGroup.${filterGroupKey}`, {
                    defaultValue: aggregation.value
                })}
                {titleIcon}
                {collapsibleGroup ? (
                    <i className="material-icons expand-icon">{expanded ? 'expand_less' : 'expand_more'}</i>
                ) : null}
            </span>
            <CSSTransition
                mountOnEnter={true}
                unmountOnExit={true}
                in={expanded}
                className="filter-wrap"
                classNames="filter-wrap"
                timeout={200}
            >
                <div>
                    {searchable ? (
                        <FilterQuickSearch
                            disabled={false}
                            value={searchQuery}
                            onChange={setSearchQuery}
                            onReset={resetSearchQuery}
                        />
                    ) : null}
                    <div className="slide-control-labels">
                        <span className="labels">
                            <span className="label cold-bgreen">{t('components.filterBar.only')}</span>
                            <span className="label cold-red">{t('components.filterBar.exclude')}</span>
                        </span>
                    </div>
                    {aggregation.buckets?.length === 0 && (
                        <div className="fe">
                            <div className="txt-smaller cold-grey" style={{ lineHeight: '20px' }}>
                                {t('components.filterBar.noFilterFound')}
                            </div>
                        </div>
                    )}
                    {loading ? (
                        <div className="fe">
                            <p className="txt-smaller cold-grey">{t('components.filterBar.loading')}</p>
                        </div>
                    ) : (
                        <>
                            <FilterGroupContextProvider
                                category={category}
                                isSearching={isSearchActive}
                                quicksearchQuery={debouncedSearchQuery}
                                quicksearchAggregations={aggregationsQuickSearchQuery.data[category]}
                            >
                                {renderFilterGroupEntry(buckets ?? [], debouncedSearchQuery, bucketLabelMapper)}
                                {children}
                                {shouldSliceBuckets ? (
                                    <>
                                        <a className="filter-more-link" onClick={() => toggleSeeAll()}>
                                            {t('components.filterBar.seeAll')}{' '}
                                            {seeAll ? (
                                                <i className="material-icons small">expand_less</i>
                                            ) : (
                                                <i className="material-icons small">expand_more</i>
                                            )}
                                        </a>
                                        {seeAll
                                            ? renderFilterGroupEntry(
                                                  bucketsAll,
                                                  debouncedSearchQuery,
                                                  bucketLabelMapper
                                              )
                                            : null}
                                    </>
                                ) : null}
                            </FilterGroupContextProvider>

                            {!expandableResults && aggregation.more ? (
                                <div className="fe">
                                    <span className="filter-label and-more">{t('components.filterBar.andMore')}</span>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </CSSTransition>
        </div>
    );
};

function renderFilterGroupEntry(
    buckets: readonly AggregationBucket[],
    debouncedSearchQuery: string,
    bucketLabelMapper: (bucket: AggregationBucket) => string
) {
    return buckets?.filter(isNotCustomBucket).map(b => {
        return (
            <FilterGroupEntry
                key={debouncedSearchQuery + '-' + b.key}
                filterKey={b.key}
                bucketLabelMapper={bucketLabelMapper}
            >
                {b.buckets.map(sub => (
                    <FilterGroupEntry
                        key={debouncedSearchQuery + '-' + sub.key}
                        filterKey={b.key}
                        filterSubKey={sub.key}
                        bucketLabelMapper={bucketLabelMapper}
                    />
                ))}
            </FilterGroupEntry>
        );
    });
}

type FilterGroupEntryProps = {
    children?: ReactNode;

    readonly filterKey: string;
    readonly filterSubKey?: string;
    bucketLabelMapper: (bucket: AggregationBucket) => string;
};

const FilterGroupEntry: FC<FilterGroupEntryProps> = ({ filterKey, filterSubKey, children, bucketLabelMapper }) => {
    const { filter, bucket, setFilter, quicksearchQuery, isSearching } = useFilterGroupContext(filterKey, filterSubKey);
    const [expanded, setExpanded] = useState(isSearching);

    const filterState: FilterSwitchState = useMemo(() => {
        return deriveFilterStateFromEntry(filter, bucket, expanded);
    }, [filter, bucket, expanded]);

    const inactive =
        (((filter.includeFilterValues?.length! > 0 ?? false) && !isBucketIncluded(bucket)) ||
            filterState === 'exclude') &&
        !(!expanded && (filterState === 'some-included' || filterState === 'mixed'));

    const filterLabelClassNames = classNames('filter-label', { inactive });

    const hasChildren = bucket.buckets.length > 0;

    const toggle = useCallback(() => setExpanded(it => !it), []);

    const filterLabel = bucketLabelMapper(bucket);

    const filterCount = (bucket.docCount | 0).toLocaleString().replace(/\D\d\d$/, '');

    return (
        <div className={classNames(hasChildren ? 'fe-with-sub' : 'fe')}>
            <div className={filterLabelClassNames}>
                <span onClick={hasChildren ? toggle : () => {}} className="text-label">
                    {quicksearchQuery ? (
                        <Highlighter
                            highlightClassName="highlight-qs"
                            autoEscape={true}
                            searchWords={quicksearchQuery.split(' ')}
                            textToHighlight={filterLabel}
                        />
                    ) : (
                        filterLabel
                    )}
                    &nbsp;
                    <span className="count">({filterCount})</span>
                    {hasChildren && (
                        <i className="material-icons normal">
                            {expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                        </i>
                    )}
                </span>
                <FilterSwitch filterState={filterState} onFilterChange={setFilter} />
            </div>
            {hasChildren && (
                <CSSTransition
                    mountOnEnter={true}
                    unmountOnExit={true}
                    in={expanded}
                    className="sub-entries"
                    classNames="sub-entries"
                    timeout={200}
                >
                    <div>{children}</div>
                </CSSTransition>
            )}
        </div>
    );
};

/**
 * Return the state of the filter entry. It takes children into account as well as their partial states.
 */
function deriveFilterStateFromEntry(
    filter: FilterSetting,
    bucket: AggregationBucket,
    expanded: boolean
): FilterSwitchState {
    if (!filter) {
        return 'neutral';
    }

    const amountOfIncludedChildren = countByPredicate(
        filter.includeFilterValues,
        val => bucket.buckets.length > 0 && val !== bucket.key && val.startsWith(bucket.key)
    );
    const amountOfExcludedChildren = countByPredicate(
        filter.excludeFilterValues,
        val => bucket.buckets.length > 0 && val !== bucket.key && val.startsWith(bucket.key)
    );

    if (!expanded && (amountOfIncludedChildren > 0 || amountOfExcludedChildren > 0)) {
        if (amountOfIncludedChildren === bucket.buckets.length && isBucketIncluded(bucket)) {
            return 'include';
        }

        if (amountOfExcludedChildren === bucket.buckets.length && isBucketExcluded(bucket)) {
            return 'exclude';
        }

        if (amountOfIncludedChildren > 0 && amountOfExcludedChildren > 0) {
            return 'mixed';
        } else {
            if (amountOfIncludedChildren > 0) {
                return 'some-included';
            } else {
                return 'some-excluded';
            }
        }
    }

    if (filter.includeFilterValues?.includes(bucket.key)) {
        return 'include';
    }

    if (filter.excludeFilterValues?.includes(bucket.key)) {
        return 'exclude';
    }

    return 'neutral';
}

function countByPredicate(arr: string[] | undefined, predicate: (val: string) => boolean): number {
    if (!arr) {
        return 0;
    }

    return arr.filter(predicate).length;
}

function isNotCustomBucket(bucket: AggregationBucket): boolean {
    return !bucket.custom;
}

function isBucketIncluded(bucket: AggregationBucket): boolean {
    return bucket.state === 'include';
}

function isBucketExcluded(bucket: AggregationBucket): boolean {
    return bucket.state === 'exclude';
}

const filterTranslatedBuckets = (
    buckets: readonly AggregationBucket[] | undefined,
    query: string,
    bucketLabelMapper: (bucket: AggregationBucket) => string
): AggregationBucket[] => {
    const keywords = splitIntoKeywords(query);
    return (
        buckets?.flatMap(bucket => {
            const filteredSubBuckets = filterTranslatedBuckets(
                bucket.buckets.length > 0 ? bucket.buckets : [],
                query,
                bucketLabelMapper
            );

            const filterLabel = bucketLabelMapper(bucket);

            const includedKeywords = keywords.filter(keyword =>
                filterLabel.toLowerCase().includes(keyword.toLowerCase())
            );
            return includedKeywords.length > 0 || filteredSubBuckets.length > 0
                ? [{ ...bucket, buckets: filteredSubBuckets }]
                : [];
        }) ?? []
    );
};

export function splitIntoKeywords(term: string): ReadonlyArray<string> {
    return term.split(/\s+/).filter(Boolean);
}

export function defaultBucketLabelMapper(t: TFunction, category: string, key: string, value: string) {
    return t(`filters:${category}.${key}`, { defaultValue: value ?? key });
}
